import { useDataPollingClient } from "../../../client/dataPollingClient";
import { usePostResolveHelpRequestClient } from "./postResolveHelpRequestClient";
import React, { useEffect, useState } from "react";
import { useAlert } from "react-alert";
import { Status } from "../../../client/status";
import { TimePassed } from "../../TimePassed";
import helpRequestNotificationAudio from "../../audio/help-request-notification-sound.mp3";
import { SoundNotification } from "../../sound-notification/SoundNotification";
import { ConfirmByDoubleClickButton } from "../../ConfirmByDoubleClickButton";
import { HelpRequestNotificationSnooze } from "./HelpRequestNotificationSnooze";
import { PanTool } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";

export const HelpRequests = ({ menuId }: { menuId: string }) => {
  const { data: helpRequests, status: helpRequestStatus } =
    useDataPollingClient<HelpRequestType[]>(
      `/api/menu/${menuId}/help-request/`
    );

  const [updatedHelpRequests, setUpdatedHelpRequests] = useState<
    HelpRequestType[]
  >([]);
  const [selectedHelpRequestId, setSelectedHelpRequestId] = useState<number>();

  const { status, resolveHelpRequest } = usePostResolveHelpRequestClient();
  const alert = useAlert();

  const handleHelpRequestResolveClick = (helpRequestId: number) => {
    setSelectedHelpRequestId(helpRequestId);
    resolveHelpRequest(helpRequestId);
  };

  useEffect(() => {
    if (helpRequests == undefined) return;
    setUpdatedHelpRequests(helpRequests);
  }, [helpRequests]);

  useEffect(() => {
    if (selectedHelpRequestId === undefined) {
      return;
    }
    if (status === Status.Success) {
      const filteredHelpRequests = updatedHelpRequests.filter(
        (updatedHelpRequest) => updatedHelpRequest.id != selectedHelpRequestId
      );
      setSelectedHelpRequestId(undefined);
      setUpdatedHelpRequests(filteredHelpRequests);
    }

    status === Status.Error && alert.error("Something went wrong!");
  }, [status, alert, updatedHelpRequests, selectedHelpRequestId]);

  if (updatedHelpRequests.length === 0) {
    return (
      <>
        <SoundNotification
          data={helpRequests}
          status={helpRequestStatus}
          soundFile={helpRequestNotificationAudio}
        />
        <HelpRequestNotificationSnooze
          dataSize={0}
          audioFile={helpRequestNotificationAudio}
        />
      </>
    );
  }

  return (
    <>
      <Box
        sx={{
          bgcolor: "#fff8f0",
          borderBottom: "1px solid #ffcc80",
          px: 2,
          pt: 1,
          pb: 1.25,
        }}
      >
        <Box display="flex" alignItems="center" gap={0.75} mb={1}>
          <PanTool sx={{ fontSize: 13, color: "#e65100" }} />
          <Typography
            variant="caption"
            fontWeight={700}
            color="#e65100"
            sx={{ letterSpacing: 0.8, textTransform: "uppercase" }}
          >
            Assistance Requests
          </Typography>
        </Box>

        <Box display="flex" gap={1} sx={{ overflowX: "auto" }}>
          {updatedHelpRequests.map((helpRequest) => (
            <Box key={helpRequest.id} sx={{ flexShrink: 0 }}>
              <ConfirmByDoubleClickButton
                onConfirmAction={() =>
                  handleHelpRequestResolveClick(helpRequest.id)
                }
              >
                <Box
                  display="flex"
                  alignItems="center"
                  gap={0.75}
                  px={1.5}
                  py={0.75}
                  data-testid="test-id-help-request-resolve-button"
                  sx={{
                    border: "1px solid #ed6c02",
                    borderRadius: "20px",
                    bgcolor: "white",
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                >
                  <PanTool sx={{ color: "#ed6c02", fontSize: 14 }} />
                  <Typography fontWeight={600} fontSize="13px">
                    {helpRequest.tableName}
                  </Typography>
                  <TimePassed dateTimeISO={helpRequest.createdAt} />
                </Box>
              </ConfirmByDoubleClickButton>
            </Box>
          ))}
        </Box>
      </Box>

      <SoundNotification
        data={helpRequests}
        status={helpRequestStatus}
        soundFile={helpRequestNotificationAudio}
      />
      <HelpRequestNotificationSnooze
        dataSize={updatedHelpRequests.length}
        audioFile={helpRequestNotificationAudio}
      />
    </>
  );
};

export type HelpRequestType = {
  id: number;
  createdAt: string;
  tableName: string;
};
